import { NavLink, useLocation } from "react-router-dom";
import { nazovRoly } from "@/lib/prava";
import {
  Folder,
  History,
  LayoutGrid,
  LogOut,
  Settings,
  Shield,
  ShoppingCart,
  Tag,
  Wrench,
  type LucideIcon,
} from "lucide-react";
import { useLogout, useMe } from "@/api/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { adresaZakaziek } from "@/lib/zoznamZakaziek";

/**
 * Header — úzka lišta 56 px: malé logo + „Rozpočet", navigácia
 * s ikonami, vpravo krúžok s iniciálami (KOLO 174).
 *
 * User si na plátne vybral variant „4a" a k nemu ikony zo „4e"; Nová
 * zákazka a Import z TopSolidu odišli z hlavičky do riadku nad tabuľkou
 * Zákaziek („pridať zákazku daj dole, aby to nebolo v hlavičke").
 *
 * KOLO 69: preč **DAEX** a **Import**, pribudla **Správa**.
 *
 * Meno a Odhlásiť sú v ponuke krúžku. Pri zapnutej auth sa meno berie
 * z `/api/auth/me`; pri vypnutej z `localStorage.tandem_user_name`
 * (nastavené cez `UserNameDialog`) a Odhlásiť nie je.
 */
export function Header(): JSX.Element {
  const { data } = useMe();
  const me = data?.data;
  const logout = useLogout();

  const localName =
    typeof window !== "undefined"
      ? localStorage.getItem("tandem_user_name") || "Hosť"
      : "Hosť";
  const prihlaseny = Boolean(me?.auth_enabled && me.user);
  const meno = prihlaseny && me?.user ? me.user.meno : localName;

  return (
    // `sticky` netreba — header je nad scrollovacím `main` v AppShelli.
    // `z-40` ostáva kvôli dropdownom a popoverom.
    <header className="z-40 shrink-0 border-b border-border bg-white">
      <div className="container mx-auto flex h-14 items-center gap-3 px-6 sm:gap-5">
        <NavLink
          to="/"
          className="flex shrink-0 items-center gap-2 text-foreground"
        >
          {/* Malé logo z `public/` (doteraz len favicon); „Rozpočet" je
              názov appky, nie prekreslené logo */}
          <img src="/tandem-logo-male.svg" alt="Tandem" className="h-7 w-7" />
          <span className="hidden font-display text-base font-semibold tracking-tight sm:inline">
            Rozpočet
          </span>
        </NavLink>
        <span aria-hidden className="hidden h-6 w-px shrink-0 bg-border sm:block" />

        {/* Na úzkom displeji sa položky nezalamujú pod lištu — posúvajú sa */}
        <nav className="flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto">
          <NavZakazky />
          <NavItem to="/cennik" label="Cenník" ikona={Tag} />
          {/* konfigurátor regálu CRC ako vlastná záložka (KOLO 237) */}
          <NavItem to="/regaly" label="Regály" ikona={LayoutGrid} />
          <NavItem to="/kovania" label="Kovania" ikona={Wrench} />
          <NavItem to="/objednavky" label="Objednávky" ikona={ShoppingCart} />
          <NavItem to="/historia" label="História" ikona={History} />
          {/* Správa je len pre admina. Pri **vypnutej** auth sa ukáže
              každému — vtedy nikto nie je admin a sekcia Používatelia je
              jediné miesto, kde je návod, ako login na serveri zapnúť. */}
          {!me?.auth_enabled || me.user?.je_admin ? (
            <NavItem to="/sprava" label="Správa" ikona={Shield} />
          ) : null}
          <NavItem to="/nastavenia" label="Nastavenia" ikona={Settings} />
        </nav>

        <div className="shrink-0">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Účet: ${meno}`}
                title={meno}
                className="flex h-10 w-10 items-center justify-center rounded-full transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <span
                  aria-hidden
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-riadok-zostava font-ui text-xs font-semibold text-riadok-zostava-text"
                >
                  {inicialy(meno)}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="normal-case tracking-normal">
                <span className="block text-[11px] font-normal text-muted-foreground">
                  Prihlásený ako
                </span>
                <span className="block text-sm font-semibold text-foreground">
                  {meno}
                </span>
                {/* Rola (KOLO 231) — čo smie upravovať, povie server pri pokuse */}
                {prihlaseny && me?.user ? (
                  <span className="block text-[11px] font-normal text-muted-foreground">
                    {nazovRoly(me.user.rola, me.user.je_admin)}
                  </span>
                ) : null}
              </DropdownMenuLabel>
              {prihlaseny ? (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    disabled={logout.isPending}
                    onSelect={() => logout.mutate()}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Odhlásiť
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}

/** „Ján Kováč" → „JK", „Hosť" → „H". */
export function inicialy(meno: string): string {
  return meno
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((slovo) => slovo[0]?.toLocaleUpperCase("sk-SK") ?? "")
    .join("");
}

/**
 * Zákazky v menu vedú na posledný filter zoznamu tejto karty prehliadača
 * (KOLO 171) — `useLocation` prekreslí odkaz po každej navigácii, lebo
 * filter sa mení na zozname. Zvýraznenie porovnáva len cestu.
 *
 * Mimo Zákaziek (Cenník, Kovania…) vedú späť do zákazky, z ktorej user
 * odišiel (KOLO 215 — `adresaZakaziek`); v editore na zoznam.
 */
function NavZakazky(): JSX.Element {
  const { pathname } = useLocation();
  return <NavItem to={adresaZakaziek(pathname)} label="Zákazky" ikona={Folder} />;
}

function NavItem({
  to,
  label,
  ikona: Ikona,
}: {
  to: string;
  label: string;
  ikona: LucideIcon;
}): JSX.Element {
  return (
    <NavLink
      to={to}
      title={label}
      className={({ isActive }) =>
        cn(
          "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full px-2 font-ui text-sm transition-colors sm:px-2.5 lg:px-3",
          isActive
            ? "bg-tandem-red-light font-semibold text-tandem-red-dark"
            : "font-medium text-muted-foreground hover:bg-muted hover:text-foreground",
        )
      }
    >
      <Ikona aria-hidden className="h-4 w-4 shrink-0" />
      {/* Pod `lg` len ikona — názov ostáva v `title` aj v DOM pre čítačku */}
      <span className="sr-only lg:not-sr-only">{label}</span>
    </NavLink>
  );
}
